<template>
  <div class="page-wrapper">
    <div class="home-page">
      <!-- 图表区域 -->
      <div class="chart-header">
        <!-- 左边 legend -->
        <div class="legend-container">
          <div
            v-for="item in legends"
            :key="item.label"
            class="legend-item"
            @click="legendClicked(item.label)"
            :style="{
              cursor: 'pointer',
              opacity: legendStatusMap[item.label] ? 1 : 0.5,
            }"
          >
            <span
              class="legend-dot"
              :style="{ backgroundColor: item.color }"
            ></span>
            <span class="legend-text">{{ item.label }}</span>
          </div>
        </div>

        <!-- 右边下拉 + 自定义 picker -->
        <div ref="pickerWrapperRef">
          <el-dropdown
            @command="handleDropdownCommand"
            @visible-change="handleVisibleChange"
            class="line-select"
          >
            <span class="el-dropdown-link">
              {{ selectedLabel }}
              <img
                :src="isDropdownOpen ? arrowGrayDownDown : arrowGrayDown"
                alt="arrow"
                class="arrow-icon"
              />
            </span>
            <template #dropdown>
              <el-dropdown-menu>
                <el-dropdown-item
                  v-for="item in dropdownOptions"
                  :key="item.id"
                  :command="item"
                  :class="{ 'is-active': selectedId === item.id }"
                  @click.stop
                >
                  {{ item.label }}
                </el-dropdown-item>
              </el-dropdown-menu>
            </template>
          </el-dropdown>

          <!-- 自定义日期选择器 -->
          <div ref="pickerWrapperRef">
            <DateRangePicker
              ref="customPickerRef"
              v-model="customRange"
              :visible="showCustomPicker"
              @close="showCustomPicker = false"
            />
          </div>
        </div>
      </div>

      <div ref="chartRef" class="chart-container"></div>
    </div>

    <div class="right-panel">
      <div class="summary-header">
        <!-- 左边信息 -->
        <div class="left-info">
          <div class="datasummary">Data summary</div>
          <div class="currency">Currency: {{ Currency }}</div>
          <div class="summary-list">
            <div
              class="summary-item"
              v-for="(item, index) in dataList"
              :key="index"
            >
              <span class="label">{{ item.label }}</span>
              <span class="value">{{ item.value }}</span>
            </div>
          </div>
        </div>

        <!-- 右边时间选择器 -->
        <div class="right-picker" ref="pickerWrapperRef">
          <div
            :class="[
              'date-display',
              visible ? 'date-display' : 'date-display-normal',
            ]"
            @click.stop="togglePicker"
          >
            <span :class="visible ? 'labeldddd' : 'labeldddd-normal'"
              >Time</span
            >
            <span class="valueddddd">{{ displayText }}</span>
            <img
              class="arrawdown"
              :src="visible ? arrowGrayDownDown : arrowGrayDown"
              alt="arrow"
            />
          </div>

          <DateRangePicker
            v-model="range"
            :visible="visible"
            @close="visible = false"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onUnmounted, watch } from "vue";
import * as echarts from "echarts";
import { getPlatformTotalData, platformTotalDataSummary } from "@/api/homeApi";
import DateRangePicker from "@/utils/DateRangePicker.vue";
import arrowGrayDown from "@/assets/timer_gray_arraw_down.svg";
import arrowGrayDownDown from "@/assets/timer_gray_arraw_downdo.svg";
import { ElMessage } from "element-plus";

const legendStatusMap = reactive({});
const chartRef = ref(null);
let chartInstance = null;

const pickerWrapperRef = ref(null);
const isDropdownOpen = ref(false);
const Currency = ref(null);
const handleVisibleChange = (visible) => (isDropdownOpen.value = visible);
function formatDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}/${m}/${d}`;
}

// 默认值：昨天
const yesterday = new Date();
yesterday.setDate(yesterday.getDate() - 1);
const selectedId = ref(2);
const dropdownOptions = [
  { id: 0, label: "Customize" },
  { id: 1, label: "Yesterday" },
  { id: 2, label: "Past 7 days" },
  { id: 3, label: "Last 30 days" },
  { id: 4, label: "Total" },
];
const platForm = ref({
  type: 2, // 0-日期查询，1-昨天，2-过去7天， 3-过去30天，4-全部
  startTime: "",
  endTime: "",
});
const datasummaryForm = ref({
  startTime: formatDate(yesterday),
  endTime: formatDate(yesterday),
});

const customRange = ref({ start: "", end: "" });
const showCustomPicker = ref(false);

const selectedLabel = computed(() => {
  const item = dropdownOptions.find((opt) => opt.id === selectedId.value);
  if (!item) return "Customize";

  if (item.id === 0 && customRange.value.start && customRange.value.end) {
    return `${customRange.value.start} - ${customRange.value.end}`;
  }
  return item.label;
});

const handleDropdownCommand = (command) => {
  selectedId.value = command.id;
  platForm.value.type = command.id;
  showCustomPicker.value = command.id === 0;

  if (command.id !== 0) {
    // 只有非自定义才立即请求
    fetchChartData();
  }
};
const customPickerRef = ref(null);

const handleClickOutside = (event) => {
  const isOutsideDropdown =
    pickerWrapperRef.value && !pickerWrapperRef.value.contains(event.target);
  const isOutsidePicker =
    customPickerRef.value && !customPickerRef.value.$el.contains(event.target);

  if (isOutsideDropdown && isOutsidePicker) {
    showCustomPicker.value = false;
    visible.value = false;
  }
};

// 图表相关
const legends = ref([
  { label: "GGR", color: "#18D87C" },
  { label: "BET", color: "#F09E1A" },
  { label: "WIN", color: "#EE3131" },
  { label: "NUM", color: "#8E67EA" },
]);

const dataList = ref([]);

const selectedLine = ref(null);

const range = ref({ start: "", end: "" });
const visible = ref(false);

const displayText = computed(() => {
  if (range.value.start && range.value.end) {
    return `${range.value.start}-${range.value.end}`;
  }
  return "请选择日期";
});

const togglePicker = () => {
  visible.value = !visible.value;
};

range.value = { start: formatDate(yesterday), end: formatDate(yesterday) };

// legend 点击事件
const legendClicked = (label) => {
  if (!chartInstance) return;
  legendStatusMap[label] = !legendStatusMap[label];
  chartInstance.dispatchAction({ type: "legendToggleSelect", name: label });
};

const initLegendStatusMap = () => {
  if (!chartInstance) return;
  const series = chartInstance.getOption().series;
  series.forEach((s) => (legendStatusMap[s.name] = true));
};

// 初始化图表
const initChart = () => {
  if (chartRef.value) {
    chartInstance = echarts.init(chartRef.value);
    window.addEventListener("resize", chartInstance.resize);
  }
};

const updateChart = (chartData) => {
  if (!chartInstance) return;

  const xAxisData = chartData.map((item) => item.dateTime);
  const ggrData = chartData.map((item) => parseFloat(item.sumGgr));
  const betData = chartData.map((item) => parseFloat(item.sumBet));
  const winData = chartData.map((item) => parseFloat(item.sumWin));
  const numData = chartData.map((item) => parseFloat(item.sumNum));

  const options = {
    tooltip: {
      trigger: "axis",
      axisPointer: {
        type: "shadow",
        shadowStyle: {
          color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: "rgba(235,228,251,0.1)" }, // 白色透明
            { offset: 1, color: "rgba(235,228,251,0.8)" }, // 浅紫透明
          ]),
        },
      },
    },
    legend: {
      show: true,
      top: -9999,
      left: -9999,
      data: ["GGR", "BET", "WIN", "NUM"],
    },
    grid: { top: 60, left: 40, right: 20, bottom: 50 },
    xAxis: {
      type: "category",
      data: xAxisData,
      axisLabel: {
        interval: 0,
        margin: 20,
        formatter: (value, index) => {
          const total = xAxisData.length;
          const step = Math.ceil(total / 4);
          if (index === total - 1) return value;
          return index % step === 0 ? value : "";
        },
      },
    },
    yAxis: { type: "value" },
    series: [
      {
        name: "GGR",
        type: "line",
        data: ggrData,
        lineStyle: { color: "#18D87C" },
        itemStyle: { color: "#18D87C" },
      },
      {
        name: "BET",
        type: "line",
        data: betData,
        lineStyle: { color: "#F09E1A" },
        itemStyle: { color: "#F09E1A" },
      },
      {
        name: "WIN",
        type: "line",
        data: winData,
        lineStyle: { color: "#EE3131" },
        itemStyle: { color: "#EE3131" },
      },
      {
        name: "NUM",
        type: "line",
        data: numData,
        lineStyle: { color: "#8E67EA" },
        itemStyle: { color: "#8E67EA" },
      },
    ],
  };

  chartInstance.setOption(options);
  initLegendStatusMap();
};

// 请求图表数据
const fetchChartData = async () => {
  try {
    const response = await getPlatformTotalData(platForm.value);
    if (response.code === 200) updateChart(response.data);
    else ElMessage.error(response.msg);
  } catch (error) {
    console.error("失败:", error);
  }
};
const fetchDataSummary = async () => {
  try {
    const response = await platformTotalDataSummary(datasummaryForm.value);
    if (response.code === 200) {
      const data = response.data;
      if (data) {
        Currency.value = data.currencyCode || "";
        dataList.value = [
          { label: "GGR", value: data.sumGgr ?? 0, color: "#18D87C" },
          { label: "BET", value: data.sumBet ?? 0, color: "#F09E1A" },
          { label: "WIN", value: data.sumWin ?? 0, color: "#EE3131" },
          { label: "NUM", value: data.sumNum ?? 0, color: "#8E67EA" },
        ];
      } else {
        Currency.value = "";
        dataList.value = [
          { label: "GGR", value: "", color: "#18D87C" },
          { label: "BET", value: "", color: "#F09E1A" },
          { label: "WIN", value: "", color: "#EE3131" },
          { label: "NUM", value: "", color: "#8E67EA" },
        ];
      }
    } else {
      ElMessage.error(response.msg);
    }
  } catch (error) {
    console.error("失败:", error);
  }
};

watch(selectedLine, () => fetchChartData());
watch(customRange, (val) => {
  if (selectedId.value === 0 && val.start && val.end) {
    platForm.value.startTime = val.start;
    platForm.value.endTime = val.end;
    platForm.value.type = 0;
    fetchChartData();
  }
});
watch(range, (val) => {
  if (val.start && val.end) {
    datasummaryForm.value.startTime = val.start;
    datasummaryForm.value.endTime = val.end;
    fetchDataSummary();
  }
});
onMounted(() => {
  initChart();
  fetchChartData();
  fetchDataSummary();
  document.addEventListener("click", handleClickOutside);
});

onUnmounted(() => {
  chartInstance?.dispose();
  document.removeEventListener("click", handleClickOutside);
});
</script>
<style scoped>
.ddddddd {
  right: 0; /* 靠右对齐 */
}
.chart-container {
  width: 100%;
  height: 400px;
  margin-top: -20px;
}

.chart-header {
  display: flex;
  justify-content: space-between; /* 左右分开 */
  align-items: center;
  flex-wrap: nowrap;
}
.line-select {
  position: relative;
  z-index: 10;
}
.legend-container {
  display: flex;
  align-items: center;
  position: relative;
  z-index: 10;
  gap: 24px;
}
.legend-item {
  margin-left: 5px;
  display: flex;
  align-items: center;
}
.legend-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 8px;
}
.legend-text {
  font-size: 12px;
}
.custom-button-group {
  margin-left: auto;
}
.active-button {
  background-color: #8e67ea;
  color: #fff;
}
/* 外层容器，水平排列 */
.page-wrapper {
  display: flex;
  gap: 10px; /* 两个 div 之间间距 */
  align-items: flex-start;
}

.home-page {
  position: relative;
  background-color: #ffffff;
  padding: 16px;
  flex: 62;
  border-radius: 16px;
}
.right-panel {
  flex: 38;
  padding: 16px;
  background: #ffffff;
  border-radius: 16px;
}

.currency {
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #9fa1a8;
  margin-top: 8px;
}

.date-picker {
  width: 100px;
}

.summary-list {
  display: flex;
  flex-direction: column;
  gap: 24px;
  margin-top: 16px;
}

.summary-item {
  display: flex;
}

.label {
  display: flex;
  align-items: center;
  font-weight: 500;
  width: 30px;
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #9fa1a8;
}
.value {
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  margin-left: 18px;
  color: #333333;
}
.datasummary {
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 18px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #333333;
}
/* 外层按钮样式 */
.date-display {
  display: flex;
  align-items: center;
  box-shadow: 0 0 0 1px #8e67ea inset; /* 内描边 */
  border-radius: 8px;
  cursor: pointer;
  background: #fff;
  height: 33px;
  width: 240px;
}

.labeldddd {
  background: #8e67ea;
  width: 48px;
  height: 33px;
  border-radius: 8px 0 0 8px;
  font-weight: 500;
  color: #fff;
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  display: flex;
  align-items: center; /* 垂直居中 */
  justify-content: center; /* 水平居中 */
}
.date-display-normal {
  display: flex;
  align-items: center;
  box-shadow: 0 0 0 1px #dce3ea inset; /* 内描边 */
  border-radius: 8px;
  cursor: pointer;
  background: #fff;
  height: 33px;
  width: 240px;
}

.labeldddd-normal {
  background: #ebe4fb;
  width: 48px;
  height: 33px;
  border-radius: 8px 0 0 8px;
  font-weight: 500;
  color: #333333;
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  display: flex;
  align-items: center; /* 垂直居中 */
  justify-content: center; /* 水平居中 */
}

.valueddddd {
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  margin-left: 10px;
  line-height: 100%;
  letter-spacing: 0%;
  color: #9fa1a8;
}

/* 隐藏原生输入框 */
:deep(.el-date-editor) {
  display: none;
}
.arrawdown {
  width: 12px;
  height: 12px;
  margin-left: 10px;
}
.summary-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start; /* 上对齐，避免被拉伸 */
  gap: 16px; /* 左右间距 */
}

.summary-header > .left-info {
  flex: 5;
}

.summary-header > .right-picker {
  flex: 5;
  display: flex;
  justify-content: flex-end;
  position: relative;
}
.el-dropdown-link {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  font-family: "Source Sans Pro", -apple-system, BlinkMacSystemFont, "Segoe UI",
    Roboto, "Helvetica Neue", Arial, sans-serif;
  font-weight: 400;
  font-style: normal;
  font-size: 12px;
  line-height: 100%;
  letter-spacing: 0%;
  outline: none !important;
  color: #9fa1a8;
}

.arrow-icon {
  width: 12px;
  height: 12px;
  margin-left: 6px; /* 让图标和 Admin 保持一点间距 */
}
/* 下拉项统一高度 */
:deep(.el-dropdown-menu__item) {
  height: 40px;
  width: 100px;
  line-height: 40px;
  font-size: 12px !important;
  color: #333333 !important;
  background-color: #ffffff !important;
}

/* hover 状态 */
:deep(.el-dropdown-menu__item:hover) {
  background-color: #f6f7f8 !important;
  color: #333333 !important;
}

/* 选中状态 */
:deep(.el-dropdown-menu__item.is-active) {
  background-color: #e9e2fa !important;
  color: #8e67ea !important;
}
.el-dropdown-menu {
  padding: 0;
  border-radius: 20px;
}
</style>
<style>
.el-popper.is-pure {
  border-radius: 8px !important;
  overflow: hidden;
}
</style>
